import { FC, RefObject, useMemo } from "react";
import { useFormikContext } from "formik";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useClientPricing } from "@hooks/billing/useClientPricing";
import { FLAT_RATE_PLANS, flatRatePlanNames } from "@common/flatRateBilling";
import { currencyFormatter } from "@common/helpers/currency";
import { fromIntToMonetary } from "@common/helpers/monetary";
import {
    isComplimentaryOrganizationClientPlan,
    isSingleEntityOnlyPlan,
} from "@common/constants/organization-billing";
import { validateClientPlanChange } from "@common/helpers/permissions/organizationRequestValidators";
import { OrganizationServicesOffered } from "@common/types/organization";
import {
    SERVICE_OPTIONS,
    servicesOfferedLabels,
} from "@components/entity/constants";
import { getMultiSelectTriggerValue } from "@components/onboarding/lib";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { getAvailablePlans } from "../AddClient/AddClientSteps/planHelpers";
import styles from "./EditClient.module.scss";
import { EditClientFormSchema, isFlatRatePlan } from "./lib";

const fields = createFormikFields<EditClientFormSchema>();

const planPriceText = ({
    plan,
    monthlyAmountCents,
}: {
    plan: FLAT_RATE_PLANS;
    monthlyAmountCents: number;
}): string | undefined => {
    if (plan === FLAT_RATE_PLANS.FREE) {
        return undefined;
    }

    if (isComplimentaryOrganizationClientPlan(plan)) {
        return "Complimentary";
    }

    return `${currencyFormatter.format(fromIntToMonetary(monthlyAmountCents))} billed monthly`;
};

export const CLIENT_NAME_DISABLED_REASON =
    "Client name can't be changed because this client manages their own billing";

const MULTI_ENTITY_PLAN_DISABLED_REASON =
    "Multi-entity clients need Plus or higher";

interface Props {
    focusInputRef: RefObject<HTMLInputElement | null>;
    billingOwnedByActiveOrganization: boolean;
    numberOfBusinessEntities: number;
    isFirmWorkspace: boolean;
}

export const EditClientFormFields: FC<Props> = ({
    focusInputRef,
    billingOwnedByActiveOrganization,
    numberOfBusinessEntities,
    isFirmWorkspace,
}) => {
    const { values, initialValues, setFieldValue } =
        useFormikContext<EditClientFormSchema>();
    const { organizationOwnedBillingPricing, clientOwnedBillingPricing } =
        useClientPricing();
    const activeOrganization = useActiveOrganization();
    const allowedPlans = activeOrganization?.allowedPlans;
    const currentPlan = values.plan ?? null;
    const selectedPlan = isFlatRatePlan(currentPlan) ? currentPlan : null;
    const initialPlanValue = initialValues.plan ?? null;
    const initialPlan = isFlatRatePlan(initialPlanValue)
        ? initialPlanValue
        : null;

    const planItems = useMemo(() => {
        const pricing = billingOwnedByActiveOrganization
            ? organizationOwnedBillingPricing
            : clientOwnedBillingPricing;

        const availablePlans = getAvailablePlans(allowedPlans);
        const plansWithCurrent =
            selectedPlan != null &&
            !availablePlans.some((plan) => plan === selectedPlan)
                ? [selectedPlan, ...availablePlans]
                : availablePlans;

        const hasMultipleEntities = numberOfBusinessEntities > 1;

        // Tax can't be moved onto, so only offer it to clients already on it.
        const selectablePlans = plansWithCurrent.filter(
            (plan) => plan !== FLAT_RATE_PLANS.TAX || initialPlan === plan,
        );

        return selectablePlans.map((plan) => {
            const planChangeError = validateClientPlanChange({
                currentPlan: initialPlan,
                plan,
            });
            const isDisabled =
                !!planChangeError ||
                (hasMultipleEntities && isSingleEntityOnlyPlan(plan));

            return {
                value: plan,
                label: flatRatePlanNames[plan],
                labelHint: planPriceText({
                    plan,
                    monthlyAmountCents: pricing[plan],
                }),
                disabled: isDisabled,
                disabledReason: isDisabled
                    ? (planChangeError ?? MULTI_ENTITY_PLAN_DISABLED_REASON)
                    : undefined,
            };
        });
    }, [
        organizationOwnedBillingPricing,
        clientOwnedBillingPricing,
        billingOwnedByActiveOrganization,
        allowedPlans,
        selectedPlan,
        initialPlan,
        numberOfBusinessEntities,
    ]);

    return (
        <>
            <fields.Text
                name="clientName"
                label="Client name"
                ref={focusInputRef}
                size="xl"
                disabled={!billingOwnedByActiveOrganization}
                className={styles.inputField}
            />

            {!billingOwnedByActiveOrganization && (
                <span>{CLIENT_NAME_DISABLED_REASON}</span>
            )}

            <FormikField<OrganizationServicesOffered[]>
                name="servicesOffered"
                label="Default services"
                control="button"
                className={styles.buttonField}
            >
                {({ helpers, field, invalid }) => (
                    <Picker.Select
                        multiple
                        items={SERVICE_OPTIONS}
                        value={field.value ?? []}
                        onValueChange={(value) => helpers.setValue(value)}
                        renderValue={(selectedServices) =>
                            getMultiSelectTriggerValue(
                                [...selectedServices],
                                SERVICE_OPTIONS.length,
                                (v) => servicesOfferedLabels[v],
                            )
                        }
                        placeholder="Select"
                        aria-invalid={invalid}
                        trigger={<Picker.Button variant="primary" size="xl" />}
                    />
                )}
            </FormikField>

            <fields.Date
                name="bookkeepingStartDate"
                label="Default Sync date"
                className={styles.buttonField}
                variant="panel-input"
                pickerLabel="Default Start date"
                placeholder="Select"
                buttonProps={{
                    variant: "primary",
                    size: "xl",
                    block: true,
                }}
            />

            {billingOwnedByActiveOrganization && (
                <FormikField<FLAT_RATE_PLANS | null>
                    name="plan"
                    label="Plan"
                    control="button"
                    className={styles.buttonField}
                >
                    {({ invalid }) => (
                        <Picker.Select
                            items={planItems}
                            value={selectedPlan}
                            onValueChange={(v) => {
                                if (v) {
                                    setFieldValue("plan", v);
                                }
                            }}
                            disabled={isFirmWorkspace}
                            placeholder="Select plan"
                            aria-invalid={invalid}
                            trigger={
                                <Picker.Button
                                    variant="primary"
                                    size="xl"
                                    block
                                />
                            }
                        />
                    )}
                </FormikField>
            )}

            {numberOfBusinessEntities > 1 && (
                <span>
                    Multi-entity is only available on Plus. Contact{" "}
                    <a href="mailto:help@kick.co">help@kick.co</a> for billing
                    updates.
                </span>
            )}

            {isFirmWorkspace && (
                <span>
                    This is your firm workspace. You can't update the plan.
                </span>
            )}
        </>
    );
};
